import { Components, Theme, ThemeOptions, alpha } from '@mui/material/styles';
import { SizeMap } from '@/themes';

const getRadioButtonTheme = (theme: Theme): ThemeOptions => {
  // RadioButton Size Map
  // Can be overridden by defining theme.size.radio in muiTheme.ts
  // Example: size: { radio: { s: { fontSize: '20px' }, ... } }
  const radioButtonSize: SizeMap = {
    s: { fontSize: '20px' },
    m: { fontSize: '24px' },
    l: { fontSize: '28px' },
    xl: { fontSize: '32px' },
  };

  const radioButtonTheme: Components<Theme>['MuiRadio'] = {
    defaultProps: {
      size: 's',
      disableRipple: true,
    },

    styleOverrides: {
      root: {
        padding: 0,
        '&:hover': {
          backgroundColor: 'transparent',
        },
        borderRadius: '100%',
      },
    },

    variants: [
      // ─────────────────────────────
      // SIZE VARIANTS
      // ─────────────────────────────
      {
        props: { size: 's' },
        style: {
          '& .MuiSvgIcon-root': {
            ...radioButtonSize.s,
          },
        },
      },
      {
        props: { size: 'm' },
        style: {
          '& .MuiSvgIcon-root': {
            ...radioButtonSize.m,
          },
        },
      },
      {
        props: { size: 'l' },
        style: {
          '& .MuiSvgIcon-root': {
            ...radioButtonSize.l,
          },
        },
      },
      {
        props: { size: 'xl' },
        style: {
          '& .MuiSvgIcon-root': {
            ...radioButtonSize.xl,
          },
        },
      },
      // ─────────────────────────────
      // COLOR VARIANTS - PRIMARY
      // ─────────────────────────────
      {
        props: { color: 'primary' },
        style: {
          color: theme.palette.M3.sys.light.primary,
          '&.Mui-checked': {
            color: theme.palette.M3.sys.light.primary,
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.M3.sys.light.primary, 0.04),
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(theme.palette.M3.sys.light.primary, 0.12),
          },
          '&.Mui-disabled': {
            color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
          },
        },
      },
      // ─────────────────────────────
      // COLOR VARIANTS - SECONDARY
      // ─────────────────────────────
      {
        props: { color: 'secondary' },
        style: {
          color: theme.palette.M3.sys.light['on-secondary-container'],
          '&.Mui-checked': {
            color: theme.palette.M3.sys.light['on-secondary-container'],
          },
          '&:hover': {
            backgroundColor: alpha(
              theme.palette.M3.sys.light['on-secondary-container'],
              0.04
            ),
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(
              theme.palette.M3.sys.light['on-secondary-container'],
              0.12
            ),
          },
          '&.Mui-disabled': {
            color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
          },
        },
      },
      // ─────────────────────────────
      // COLOR VARIANTS - SUCCESS
      // ─────────────────────────────
      {
        props: { color: 'success' },
        style: {
          color: theme.palette.State.success,
          '&.Mui-checked': {
            color: theme.palette.State.success,
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.State.success, 0.04),
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(theme.palette.State.success, 0.12),
          },
          '&.Mui-disabled': {
            color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
          },
        },
      },
      // ─────────────────────────────
      // COLOR VARIANTS - ERROR
      // ─────────────────────────────
      {
        props: { color: 'error' },
        style: {
          color: theme.palette.State.error,
          '&.Mui-checked': {
            color: theme.palette.State.error,
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.State.error, 0.04),
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(theme.palette.State.error, 0.12),
          },
          '&.Mui-disabled': {
            color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
          },
        },
      },
      // ─────────────────────────────
      // COLOR VARIANTS - WARNING
      // ─────────────────────────────
      {
        props: { color: 'warning' },
        style: {
          color: theme.palette.State.warning,
          '&.Mui-checked': {
            color: theme.palette.State.warning,
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.State.warning, 0.04),
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(theme.palette.State.warning, 0.12),
          },
          '&.Mui-disabled': {
            color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
          },
        },
      },
      // ─────────────────────────────
      // COLOR VARIANTS - INFO
      // ─────────────────────────────
      {
        props: { color: 'info' },
        style: {
          color: theme.palette.State.info,
          '&.Mui-checked': {
            color: theme.palette.State.info,
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.State.info, 0.04),
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(theme.palette.State.info, 0.12),
          },
          '&.Mui-disabled': {
            color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
          },
        },
      },
      // ─────────────────────────────
      // COLOR VARIANTS - DESTRUCTIVE
      // ─────────────────────────────
      {
        props: { color: 'destructive' },
        style: {
          color: theme.palette.State.error,
          '&.Mui-checked': {
            color: theme.palette.State.error,
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.State.error, 0.04),
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(theme.palette.State.error, 0.12),
          },
          '&.Mui-disabled': {
            color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
          },
        },
      },
    ],
  };

  return {
    components: {
      MuiRadio: radioButtonTheme,
    },
  };
};

export default getRadioButtonTheme;
